import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ViewTenantEmailBanner.scss';
import { getImageUrl } from 'application-types';

type ViewTenantEmailBannerProps = {
  visitorEmailBannerId: string | null | undefined;
};

const translationKeys = {
  noBanner:
    'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.noBanner',
  loading:
    'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.loading',
  failedToLoad:
    'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.failedToLoad',
  title:
    'web.admin.channel.settings.tenantManagement.tenant.details.tenantEmailBannerSection.title',
};

export const ViewTenantEmailBanner = ({
  visitorEmailBannerId,
}: ViewTenantEmailBannerProps) => {
  const { t } = useTranslation();
  const [imageStatus, setImageStatus] = useState<
    'loading' | 'loaded' | 'error'
  >('loading');

  useEffect(() => {
    setImageStatus('loading');
  }, [visitorEmailBannerId]);

  const hasBanner =
    visitorEmailBannerId && typeof visitorEmailBannerId === 'string';

  if (!hasBanner) {
    return (
      <div className={styles.EmptyState} data-test="empty-state">
        {t(translationKeys.noBanner)}
      </div>
    );
  }

  const bannerUrl = getImageUrl(visitorEmailBannerId);

  return (
    <div className={styles.PreviewContainer} data-test="banner-preview">
      {imageStatus === 'loading' && (
        <div
          className={styles.LoadingState}
          data-test="banner-loading"
          aria-live="polite"
        >
          {t(translationKeys.loading)}
        </div>
      )}
      {imageStatus === 'error' && (
        <div
          className={styles.FailedState}
          data-test="banner-failed-to-load"
          role="alert"
        >
          {t(translationKeys.failedToLoad)}
        </div>
      )}
      <img
        data-test="tenant-email-banner-preview"
        src={bannerUrl}
        alt={t(translationKeys.title)}
        className={
          imageStatus === 'loaded'
            ? styles.PreviewImage
            : `${styles.PreviewImage} ${styles.PreviewImageHidden}`
        }
        onLoad={() => setImageStatus('loaded')}
        onError={() => setImageStatus('error')}
      />
    </div>
  );
};
